import React from 'react';

// ─── Helpers ──────────────────────────────────────────────────────────────

export const safeAsync = (fn) => async (...args) => { try { await fn(...args); } catch (err) { console.error('[Personnel]', err); alert('Error: ' + (err.message || 'Unknown error')); } };

export const sp = (v, fallback = {}) => { if (!v) return fallback; if (typeof v === 'object') return v; try { return JSON.parse(v); } catch { return fallback; } };

// ─── Color Palette (Athenahealth scheme) ──────────────────────────────────────

export const PRIMARY_COLOR = '#2E8B98';
export const PRIMARY_LIGHT = '#3CA0AE';
export const PRIMARY_HOVER = '#256F7A';
export const SUCCESS_COLOR = '#62BB46';
export const WARNING_COLOR = '#F26522';
export const DANGER_COLOR = '#DC3545';
export const NEUTRAL_COLOR = '#6C757D';
export const BG_COLOR = '#F8F9FA';
export const TEXT_PRIMARY = '#212529';
export const TEXT_SECONDARY = '#6C757D';

// ─── Job Categories ───────────────────────────────────────────────────────────

export const JOB_CATEGORIES = {
  'Board of Directors': { color: '#6b21a8', icon: 'Crown', titles: ['Board Chair', 'Vice Chair', 'Board Member', 'Board Secretary', 'Board Treasurer', 'Independent Director', 'Non-Executive Director'] },
  'Clinical Leadership': { color: '#2E8B98', icon: 'Shield', titles: ['Medical Director', 'Chief Medical Officer', 'Department Head', 'Clinical Supervisor'] },
  'Physicians': { color: '#256F7A', icon: 'Stethoscope', titles: ['Attending Physician', 'Resident Physician', 'Fellow', 'Consulting Physician', 'Specialist'] },
  'Nursing': { color: '#62BB46', icon: 'Heart', titles: ['Registered Nurse', 'Nurse Practitioner', 'Licensed Practical Nurse', 'Charge Nurse', 'Nurse Manager'] },
  'Allied Health': { color: '#4FA038', icon: 'Activity', titles: ['Medical Assistant', 'Phlebotomist', 'Radiology Technologist', 'Respiratory Therapist', 'Physical Therapist'] },
  'Research': { color: '#F26522', icon: 'Microscope', titles: ['Principal Investigator', 'Research Coordinator', 'Lab Technician', 'Data Analyst', 'Biostatistician'] },
  'Administration': { color: '#DC3545', icon: 'Briefcase', titles: ['Practice Administrator', 'Office Manager', 'HR Coordinator', 'Compliance Officer', 'Quality Manager'] },
  'IT & Engineering': { color: '#6C757D', icon: 'Monitor', titles: ['Systems Administrator', 'Network Engineer', 'Software Developer', 'Database Administrator', 'Security Analyst'] },
  'Billing & Finance': { color: '#256F7A', icon: 'DollarSign', titles: ['Billing Specialist', 'Revenue Cycle Manager', 'Coding Specialist', 'Financial Analyst', 'Collections Coordinator'] },
  'Front Office': { color: '#2E8B98', icon: 'PhoneCall', titles: ['Receptionist', 'Patient Access Rep', 'Scheduling Coordinator', 'Medical Records Clerk', 'Registration Specialist'] },
};

// ─── Data Constants ───────────────────────────────────────────────────────────

export const DEPARTMENTS = ['Cardiology', 'Pediatrics', 'Internal Medicine', 'Surgery', 'Orthopedics', 'Oncology', 'Neurology', 'Emergency Medicine', 'Radiology', 'Pathology', 'Psychiatry', 'Obstetrics & Gynecology', 'Urology', 'Gastroenterology', 'Rheumatology', 'Infectious Disease', 'Pulmonology', 'Nephrology', 'Dermatology', 'Administration', 'IT', 'Billing', 'Human Resources'];

export const LOCATIONS = ['Main Campus', 'Outpatient Clinic - Downtown', 'Satellite Office - North', 'Satellite Office - South', 'Remote', 'Hybrid'];

export const PERSONNEL_STATUS = ['active', 'inactive', 'on_leave', 'terminated'];

export const STATUS_COLORS = {
  active: '#62BB46',
  inactive: '#6C757D',
  on_leave: '#F26522',
  terminated: '#DC3545',
};

export const EMPLOYMENT_TYPES = ['Full-Time', 'Part-Time', 'Contract', 'Temporary', 'Locum Tenens'];

// ─── Neumorphic Style Constants ───────────────────────────────────────────────

export const neuShadow = '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.8)';
export const neuShadowSm = '3px 3px 6px rgba(186,199,216,0.45), -3px -3px 6px rgba(255,255,255,0.8)';
export const neuShadowInset = 'inset 3px 3px 6px rgba(186,199,216,0.45), inset -3px -3px 6px rgba(255,255,255,0.8)';
export const neuShadowPressed = 'inset 4px 4px 8px rgba(186,199,216,0.45), inset -4px -4px 8px rgba(255,255,255,0.8)';

// ─── Style Objects ────────────────────────────────────────────────────────────

export const inp = { padding: '8px 12px', borderRadius: 10, border: 'none', fontSize: 13, width: '100%', background: '#F8F9FA', boxShadow: neuShadowInset, outline: 'none', color: '#212529', transition: 'box-shadow 0.15s ease' };

export const btnPrimary = {
  padding: '8px 16px', borderRadius: 10, border: 'none',
  background: 'linear-gradient(145deg, #3CA0AE, #256F7A)', color: '#fff',
  cursor: 'pointer', fontSize: 12, fontWeight: 600,
  display: 'inline-flex', alignItems: 'center', gap: 5,
  boxShadow: `${neuShadow}, inset 0 2px 0 rgba(255,255,255,0.2), inset 0 -2px 0 rgba(0,0,0,0.1)`,
  transition: 'all 0.15s cubic-bezier(0.4,0,0.2,1)', position: 'relative',
};

export const btnSm = {
  padding: '6px 12px', borderRadius: 8, border: 'none',
  background: '#F8F9FA', color: '#6C757D',
  cursor: 'pointer', fontSize: 12, fontWeight: 500,
  display: 'inline-flex', alignItems: 'center', gap: 4,
  boxShadow: neuShadowSm,
  transition: 'all 0.15s cubic-bezier(0.4,0,0.2,1)', position: 'relative',
};

export const btnSuccess = {
  padding: '6px 12px', borderRadius: 8, border: 'none',
  background: 'linear-gradient(145deg, #7ACF5D, #4FA038)', color: '#fff',
  cursor: 'pointer', fontSize: 12, fontWeight: 600,
  display: 'inline-flex', alignItems: 'center', gap: 4,
  boxShadow: `${neuShadowSm}, inset 0 1px 0 rgba(255,255,255,0.2)`,
  transition: 'all 0.15s cubic-bezier(0.4,0,0.2,1)',
};

export const btnDanger = {
  padding: '6px 12px', borderRadius: 8, border: 'none',
  background: 'linear-gradient(145deg, #E4606D, #B02A37)', color: '#fff',
  cursor: 'pointer', fontSize: 12, fontWeight: 600,
  display: 'inline-flex', alignItems: 'center', gap: 4,
  boxShadow: `${neuShadowSm}, inset 0 1px 0 rgba(255,255,255,0.2)`,
  transition: 'all 0.15s cubic-bezier(0.4,0,0.2,1)',
};

export const btnWarning = {
  padding: '6px 12px', borderRadius: 8, border: 'none',
  background: 'linear-gradient(145deg, #F47C3B, #D65020)', color: '#fff',
  cursor: 'pointer', fontSize: 12, fontWeight: 600,
  display: 'inline-flex', alignItems: 'center', gap: 4,
  boxShadow: `${neuShadowSm}, inset 0 1px 0 rgba(255,255,255,0.2)`,
  transition: 'all 0.15s cubic-bezier(0.4,0,0.2,1)',
};

export const neuCard = { background: '#F8F9FA', borderRadius: 14, border: 'none', boxShadow: neuShadow, padding: 20, marginBottom: 16 };

export const neuCardInner = { background: '#F8F9FA', borderRadius: 10, boxShadow: neuShadowInset, padding: 12 };

export const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };

export const modalStyle = { background: '#F8F9FA', borderRadius: 20, width: 560, maxHeight: '85vh', overflow: 'auto', padding: 28, boxShadow: '12px 12px 24px rgba(186,199,216,0.65), -12px -12px 24px rgba(255,255,255,0.9)' };

export const modalHeader = { display: 'flex', justifyContent: 'space-between', marginBottom: 20 };

export const modalTitle = { fontSize: 16, fontWeight: 700, margin: 0, color: '#212529' };

export const closeBtn = { background: '#F8F9FA', border: 'none', cursor: 'pointer', borderRadius: 8, padding: 4, boxShadow: neuShadowSm };

export const cardWrap = { background: '#F8F9FA', borderRadius: 14, border: 'none', overflow: 'hidden', boxShadow: neuShadow };

export const emptyMsg = { padding: 40, textAlign: 'center', color: '#ADB5BD', fontSize: 13 };

// ─── Components ───────────────────────────────────────────────────────────────

export function Badge({ text, color, bg }) {
  return <span style={{ background: bg || `${color}18`, color, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{text}</span>;
}

export function KPI({ icon: Icon, label, value, color = '#2E8B98', sub, trend }) {
  return (
    <div style={{ background: '#F8F9FA', borderRadius: 12, padding: '14px 18px', border: 'none', boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)', flex: '1 1 140px', minWidth: 130 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: 11, color: '#6C757D', fontWeight: 500 }}>{label}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Icon size={14} color={color} />
          {trend && <span style={{ fontSize: 11, color: trend > 0 ? '#62BB46' : '#DC3545', fontWeight: 600 }}>{trend > 0 ? '↑' : '↓'} {Math.abs(trend)}%</span>}
        </div>
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: '#212529' }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: '#ADB5BD', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export function StatusDot({ status }) {
  const color = STATUS_COLORS[status] || '#6C757D';
  const labels = { active: 'Active', inactive: 'Inactive', on_leave: 'On Leave', terminated: 'Terminated' };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, boxShadow: `0 0 8px ${color}80` }} />
      <span style={{ fontSize: 12, color, fontWeight: 500 }}>{labels[status] || status}</span>
    </div>
  );
}

export function AvatarCircle({ firstName, lastName, color = '#2E8B98', size = 40 }) {
  const initials = getInitials(firstName, lastName);
  const bgColor = color + '20';
  return (
    <div style={{
      width: size,
      height: size,
      borderRadius: '50%',
      background: `linear-gradient(135deg, ${color}, ${color}99)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      fontSize: size / 2.5,
      fontWeight: 700,
      boxShadow: `0 2px 8px ${color}40`,
    }}>
      {initials}
    </div>
  );
}

export function NeuButton({ children, variant = 'primary', size = 'md', onClick, disabled, style }) {
  const baseStyles = {
    primary: btnPrimary,
    success: btnSuccess,
    danger: btnDanger,
    warning: btnWarning,
    sm: btnSm,
    ghost: { padding: '6px 12px', borderRadius: 8, border: 'none', background: 'transparent', color: '#6C757D', cursor: 'pointer', fontSize: 12, fontWeight: 500, transition: 'all 0.15s ease' },
  };

  const selectedStyle = baseStyles[variant] || btnPrimary;
  const sizeAdjustments = size === 'sm' ? { padding: '6px 10px', fontSize: 11 } : {};

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        ...selectedStyle,
        ...sizeAdjustments,
        ...style,
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {children}
    </button>
  );
}

export function NeuInput({ value, onChange, placeholder, icon: Icon, style }) {
  return (
    <div style={{ position: 'relative', width: '100%', ...style }}>
      {Icon && <Icon size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6C757D', pointerEvents: 'none' }} />}
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        style={{
          ...inp,
          paddingLeft: Icon ? 36 : 12,
        }}
      />
    </div>
  );
}

export function NeuSelect({ value, onChange, options, placeholder, style }) {
  return (
    <select
      value={value}
      onChange={onChange}
      style={{
        ...inp,
        ...style,
        appearance: 'none',
        paddingRight: 28,
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236C757D' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 12px center',
        backgroundColor: '#F8F9FA',
      }}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options && options.map((opt, i) => (
        <option key={i} value={typeof opt === 'string' ? opt : opt.value}>
          {typeof opt === 'string' ? opt : opt.label}
        </option>
      ))}
    </select>
  );
}

export function SectionHeader({ title, subtitle, icon: Icon, action }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {Icon && <Icon size={24} color="#2E8B98" />}
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: '#212529' }}>{title}</h2>
          {subtitle && <p style={{ fontSize: 12, color: '#6C757D', margin: '4px 0 0 0' }}>{subtitle}</p>}
        </div>
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, subtitle }) {
  return (
    <div style={{ ...emptyMsg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      {Icon && <Icon size={48} color="#ADB5BD" style={{ marginBottom: 16 }} />}
      <h3 style={{ fontSize: 14, fontWeight: 600, color: '#212529', margin: '0 0 8px 0' }}>{title}</h3>
      {subtitle && <p style={{ fontSize: 12, color: '#ADB5BD', margin: 0 }}>{subtitle}</p>}
    </div>
  );
}

export function Tooltip({ children, text }) {
  const [show, setShow] = React.useState(false);

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      {children}
      {show && (
        <div
          style={{
            position: 'absolute',
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#212529',
            color: '#fff',
            padding: '6px 10px',
            borderRadius: 6,
            fontSize: 11,
            fontWeight: 500,
            whiteSpace: 'nowrap',
            marginBottom: 8,
            zIndex: 100,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}
        >
          {text}
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '4px solid transparent',
              borderRight: '4px solid transparent',
              borderTop: '4px solid #212529',
            }}
          />
        </div>
      )}
    </div>
  );
}

// ─── Helper Functions ──────────────────────────────────────────────────────────

export function getCategoryForTitle(jobTitle) {
  for (const [category, data] of Object.entries(JOB_CATEGORIES)) {
    if (data.titles.includes(jobTitle)) {
      return { category, color: data.color, icon: data.icon };
    }
  }
  return { category: 'Other', color: '#6C757D', icon: 'User' };
}

export function formatDate(dateStr) {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function formatPhone(phone) {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length !== 10) return phone;
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
}

export function getInitials(firstName, lastName) {
  const first = (firstName || '').charAt(0).toUpperCase();
  const last = (lastName || '').charAt(0).toUpperCase();
  return first + last || 'U';
}

export function sanitize(str) {
  if (typeof str !== 'string') return '';
  return str.replace(/[<>]/g, '').trim();
}

// ─── Demo Data Generator ──────────────────────────────────────────────────────

const FIRST_NAMES = ['James', 'Mary', 'Robert', 'Patricia', 'Michael', 'Jennifer', 'William', 'Linda', 'David', 'Barbara', 'Richard', 'Susan', 'Joseph', 'Jessica', 'Thomas', 'Sarah', 'Charles', 'Karen', 'Christopher', 'Lisa', 'Daniel', 'Nancy', 'Matthew', 'Betty', 'Anthony', 'Margaret', 'Mark', 'Sandra', 'Donald', 'Ashley', 'Steven', 'Kimberly', 'Paul', 'Emily', 'Andrew', 'Donna', 'Joshua', 'Michelle', 'Kenneth', 'Carol', 'Kevin', 'Amanda', 'Brian', 'Melissa', 'Edward', 'Deborah', 'Ronald', 'Stephanie', 'Anthony', 'Rebecca', 'Frank', 'Sharon'];

const LAST_NAMES = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Gonzalez', 'Wilson', 'Anderson', 'Thomas', 'Taylor', 'Moore', 'Jackson', 'Martin', 'Lee', 'Perez', 'Thompson', 'White', 'Harris', 'Sanchez', 'Clark', 'Ramirez', 'Lewis', 'Robinson', 'Young', 'Allen', 'King', 'Wright', 'Scott', 'Torres', 'Peterson', 'Phillips', 'Campbell', 'Parker', 'Evans', 'Edwards', 'Collins', 'Reyes', 'Stewart', 'Morris', 'Morales', 'Murphy', 'Cook'];

export function generateDemoPersonnel(count = 50) {
  const personnel = [];
  const allCategories = Object.keys(JOB_CATEGORIES);
  const supervisorIds = [];

  for (let i = 0; i < count; i++) {
    const id = 1001 + i;
    const firstName = FIRST_NAMES[Math.floor(Math.random() * FIRST_NAMES.length)];
    const lastName = LAST_NAMES[Math.floor(Math.random() * LAST_NAMES.length)];
    const jobCategory = allCategories[Math.floor(Math.random() * allCategories.length)];
    const jobTitle = JOB_CATEGORIES[jobCategory].titles[Math.floor(Math.random() * JOB_CATEGORIES[jobCategory].titles.length)];
    const department = DEPARTMENTS[Math.floor(Math.random() * DEPARTMENTS.length)];
    const location = LOCATIONS[Math.floor(Math.random() * LOCATIONS.length)];
    const status = Math.random() < 0.8 ? 'active' : Math.random() < 0.5 ? 'on_leave' : Math.random() < 0.5 ? 'inactive' : 'terminated';
    const hireDate = new Date(2018 + Math.floor(Math.random() * 8), Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1).toISOString().split('T')[0];
    const birthDate = new Date(1965 + Math.floor(Math.random() * 40), Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1).toISOString().split('T')[0];
    const phone = `(${String(Math.floor(Math.random() * 900) + 100).padStart(3, '0')}) ${String(Math.floor(Math.random() * 900) + 100).padStart(3, '0')}-${String(Math.floor(Math.random() * 9000) + 1000).padStart(4, '0')}`;
    const email = `${firstName.toLowerCase()}.${lastName.toLowerCase()}@zignagenix.com`;
    const employmentType = EMPLOYMENT_TYPES[Math.floor(Math.random() * EMPLOYMENT_TYPES.length)];
    const salary = Math.floor(Math.random() * 150000) + 50000;
    const supervisor = supervisorIds.length > 0 && Math.random() < 0.7 ? supervisorIds[Math.floor(Math.random() * supervisorIds.length)] : null;

    personnel.push({
      id,
      // camelCase (used by ReportsTab, DirectoryTab, OrgChartTab, ProfileModal)
      firstName,
      lastName,
      jobTitle,
      jobCategory,
      hireDate,
      birthDate,
      employmentType,
      // snake_case aliases (used by DashboardTab, backend DB records)
      first_name: firstName,
      last_name: lastName,
      job_title: jobTitle,
      job_category: jobCategory,
      hire_date: hireDate,
      date_of_birth: birthDate,
      birth_date: birthDate,
      employee_id: `EMP-${id}`,
      employment_type: employmentType,
      // composite name field (used by LeaveTab, OrgChartTab)
      name: `${firstName} ${lastName}`,
      // common fields
      department,
      location,
      status,
      phone,
      email,
      salary,
      supervisor,
      licenseNumber: `LIC${String(id).padStart(6, '0')}`,
      licenseExpiry: new Date(2025 + Math.floor(Math.random() * 5), Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1).toISOString().split('T')[0],
      certifications: Math.random() < 0.6 ? ['Board Certified', 'CPR'] : [],
      notes: '',
    });

    // Track supervisors for later references
    if (jobTitle.includes('Manager') || jobTitle.includes('Director') || jobTitle.includes('Lead')) {
      supervisorIds.push(id);
    }
  }

  return personnel;
}
